import { stripStyleUrlFunction } from '@common/html/util/style';
import { EditorEvent } from '@portal-core/project-files/events/editor-event.event';
import { NodeViewBase } from '@portal-core/text-editor/node-views/node-view.base';
import { GetPosForNodeView } from '@portal-core/text-editor/node-views/types/nodeview-get-pos.type';
import { BidiBridge } from '@portal-core/util/bidi-bridge';
import { Node } from 'prosemirror-model';
import { Decoration, EditorView, NodeView } from 'prosemirror-view';

export class ExpandingNodeView extends NodeViewBase implements NodeView {
  private onToggleExpanding: (event: CustomEvent) => void;

  /** Opt-in to sync the node when the stylesheet changes. */
  get syncNodeOnStylesheetChanges(): boolean {
    return true;
  }

  constructor(
    node: Node,
    editorView: EditorView,
    getPos: GetPosForNodeView,
    decorations: Decoration[],
    nodeViewBridge: BidiBridge<EditorEvent, EditorEvent>
  ) {
    super(node, editorView, getPos, decorations, nodeViewBridge);

    // Listen for toggle events from the expanding head node view
    this.onToggleExpanding = event => {
      // Stop propagation so a nested expanding's toggle doesn't also toggle its ancestor expandings
      event.stopPropagation();
      this.dom.toggleAttribute('mc-lwe-expanding-open');
      this.syncNode();
    };

    this.dom.addEventListener('toggle-expanding', this.onToggleExpanding);

    // Create the contentDOM element and sync the node
    this.contentDOM = this.dom;
    this.syncNode();

    // Sync the node again once the expanding head is ready
    this.syncNodeWhenReady(() => !!this.contentDOM.querySelector<HTMLElement>('MadCap\\:expandingHead'));
  }

  destroy() {
    this.dom.removeEventListener('toggle-expanding', this.onToggleExpanding);
    super.destroy();
  }

  ignoreMutation(mutation: MutationRecord): boolean {
    // Ignore toggling the expanding open attribute
    if (mutation.type === 'attributes' && mutation.target === this.dom && mutation.attributeName === 'mc-lwe-expanding-open') {
      return true;
    }

    return super.ignoreMutation(mutation);
  }

  syncNode() {
    const expandingHeadDOM = this.contentDOM.querySelector<HTMLElement>('MadCap\\:expandingHead');

    if (expandingHeadDOM) {
      // Grab the mc styles for the expanding image
      const closedImage = getComputedStyle(this.dom).getPropertyValue('--mc-closed-image') || 'url(assets/images/editor/flare/expandingclosed.gif)';
      const openImage = getComputedStyle(this.dom).getPropertyValue('--mc-open-image') || 'url(assets/images/editor/flare/expandingopen.gif)';
      const imageSpacing = getComputedStyle(this.dom).getPropertyValue('--mc-image-spacing') || '5px';
      const imagePosition = getComputedStyle(this.dom).getPropertyValue('--mc-image-position') || 'right';

      const isOpen = this.dom.hasAttribute('mc-lwe-expanding-open');
      let imageSrc = isOpen ? stripStyleUrlFunction(openImage) : stripStyleUrlFunction(closedImage);
      if (imagePosition === 'none') {
        imageSrc = null;
      }

      // Grab the dom elements to update
      const buttonDOM = this.contentDOM.querySelector<HTMLButtonElement>('.mc-lwe-expandinghead-button');
      const imgDOM = this.contentDOM.querySelector<HTMLImageElement>('.mc-lwe-expandinghead-image');
      const expandingBodyDOM = this.contentDOM.querySelector<HTMLElement>('MadCap\\:expandingBody');

      // Update the body element
      expandingBodyDOM.setAttribute('contenteditable', isOpen ? 'true' : 'false');

      // Update the img and button elements
      if (imageSrc) {
        imgDOM.src = imageSrc;

        if (imagePosition === 'left') {
          buttonDOM.style.removeProperty('margin-left');
          buttonDOM.style.setProperty('margin-right', imageSpacing);
          // Ensure the button is the first child
          if (buttonDOM !== expandingHeadDOM.firstChild) {
            expandingHeadDOM.prepend(buttonDOM);
          }
        } else if (imagePosition === 'right') {
          buttonDOM.style.setProperty('margin-left', imageSpacing);
          buttonDOM.style.removeProperty('margin-right');
          // Ensure the button is the last child
          if (buttonDOM !== expandingHeadDOM.lastChild) {
            expandingHeadDOM.appendChild(buttonDOM);
          }
        }
      } else {
        imgDOM.removeAttribute('src');
        buttonDOM.style.removeProperty('margin-left');
        buttonDOM.style.removeProperty('margin-right');
      }
    }
  }
}
